import { syncPlaybookTool } from '../model/playbook-tools';
import React, { createContext, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { debounce } from 'lodash';

import {
    journeyCreate, journeyDefinitionSave, journeyGet,
    getWorkflowHistoryStatus, redoWorkflowChange, undoWorkflowChange,
} from '../../../services/model/journey-service';
import { getAgentAction, saveAgentActionSettings } from '../../../services/prompt-ai';
import { startPlaybookGeneration, getPlaybookGenerationStatus, isGenerationActive } from '../../../services/playbook-generation';
import { compilePlaybook } from '../compiler/compile-playbook';
import {
    AUTHORING_MODE_PLAYBOOK,
    createEmptyPlaybook,
    createSection,
    appendBranch,
    createStep,
    changeStepKind,
    isPlaybookRecord,
} from '../model/playbook-model';

/**
 * Holds the playbook being edited and owns the save pipeline.
 *
 * This is the playbook counterpart of WorkflowCanvasContextProvider: same
 * responsibilities (load, mutate, save, report errors), but the
 * state it holds is a playbook document rather than ReactFlow nodes and edges.
 * Both save through `journeyDefinitionSave`, so a playbook and a canvas workflow
 * are the same kind of record to the backend and the runtime.
 *
 * Like the canvas, saving is manual by default: edits are kept locally until the
 * author saves (`saveNow`), and `hasUnsavedChanges` tells the editor when there
 * is something to save. With auto save on, edits save shortly after they are
 * made. Every manual save is a point in the journey's server-side history, which
 * undo/redo step through (the same workflow-history API the canvas uses).
 */

const AUTO_SAVE_DELAY = 800;
// Remembered across sessions, separately from the canvas preference.
export const AUTO_SAVE_STORAGE_KEY = 'botcircuits-playbook-auto-save';

const readAutoSave = () => {
    try { return JSON.parse(localStorage.getItem(AUTO_SAVE_STORAGE_KEY)) === true; }
    catch (e) { return false; }
};

export const PlaybookContext = createContext({});

/** What the editor found when it loaded the journey. */
export const LOAD_STATE = {
    LOADING: 'LOADING',
    READY: 'READY',
    // The journey was built in the drag-drop canvas. Opening it here and saving
    // would replace its nodes/edges with a compiled chain and lose the layout,
    // so the editor refuses and points the user at the canvas instead.
    CANVAS_OWNED: 'CANVAS_OWNED',
    FAILED: 'FAILED',
};

export function PlaybookContextProvider({ children }) {
    const [aiGenStatus, setAiGenStatus] = useState(null);
    const [aiStarting, setAiStarting] = useState(false);
    const [aiPollError, setAiPollError] = useState('');
    const generationLock = useRef(false);
    const pendingSaves = useRef(new Set());
    const [action, setAction] = useState(null);
    const [settingsSaving, setSettingsSaving] = useState(false);
    const settingsBusy = useRef(false);
    const [playbook, setPlaybook] = useState(null);
    const [metadata, setMetadata] = useState({ appId: null, journeyId: null, slots: {} });
    const [loadState, setLoadState] = useState(LOAD_STATE.LOADING);

    const [selectedStepId, setSelectedStepId] = useState(null);
    const [compileErrors, setCompileErrors] = useState([]);
    const [compileWarnings, setCompileWarnings] = useState([]);
    const [saveError, setSaveError] = useState(null);
    const [saveSuccess, setSaveSuccess] = useState(false);
    const [syncing, setSyncing] = useState(false);
    // The playbook as last loaded or saved. Every edit makes a new object, so
    // anything else in `playbook` is an unsaved change.
    const [savedPlaybook, setSavedPlaybook] = useState(null);
    const [autoSaveEnabled, setAutoSaveEnabled] = useState(readAutoSave);
    const [history, setHistory] = useState({ canUndo: false, canRedo: false, busy: false });
    const historyTimer = useRef(null);
    const autoSaveRef = useRef(null);

    const errorsByStep = useMemo(() => {
        const map = {};
        compileErrors.forEach(e => {
            if (!e.stepId) return;
            map[e.stepId] = map[e.stepId] || [];
            map[e.stepId].push(e.message);
        });
        return map;
    }, [compileErrors]);

    // `load` needs to save (to claim a brand new playbook) but `save` is defined
    // below it. A ref keeps `load`'s identity stable - it is called from an
    // effect keyed on appId/journeyId - without depending on declaration order.
    const saveRef = useRef(null);

    const load = useCallback(async (appId, journeyId) => {
        // An auto save still waiting would write the old playbook over this one.
        autoSaveRef.current?.cancel();
        setLoadState(LOAD_STATE.LOADING);
        try {
            const generationStatus = await getPlaybookGenerationStatus(appId, journeyId);
            const [journey, savedAction] = await Promise.all([
                journeyGet(appId, journeyId), getAgentAction(appId, journeyId),
            ]);
            setAction(savedAction);
            setAiGenStatus(generationStatus || null);
            generationLock.current = isGenerationActive(generationStatus);
            if (!generationLock.current) {
                try { await syncPlaybookTool(appId, savedAction); }
                catch (error) { setSaveError(error.message); }
            }
            const stm = journey?.stm || {};

            setMetadata({ appId, journeyId, slots: journey?.slots || {} });

            if (stm?.metadata?.playbook) {
                setPlaybook(stm.metadata.playbook);
                setSavedPlaybook(stm.metadata.playbook);
                setCompileErrors([]);
                setCompileWarnings([]);
                setLoadState(LOAD_STATE.READY);
                return;
            }

            // A canvas workflow with real content: refuse rather than clobber it.
            if ((stm?.metadata?.nodes || []).length > 1) {
                setLoadState(LOAD_STATE.CANVAS_OWNED);
                return;
            }

            // Empty or brand new - this journey is ours to author.
            const fresh = createEmptyPlaybook();
            setPlaybook(fresh);
            setSavedPlaybook(fresh);
            setLoadState(LOAD_STATE.READY);

            // Claim it straight away. Saving the (empty) definition is what
            // creates the journey record; without it, adding a variable before
            // the first edit fails with "journey not found".
            if (!stm?.metadata?.playbook && !generationLock.current) {
                saveRef.current?.(fresh, appId, journeyId, true);
            }
        } catch (e) {
            console.error('playbook load failed', e);
            setSaveError(e.message || 'Failed to load this playbook');
            setLoadState(LOAD_STATE.FAILED);
        }
    }, []);

    /**
     * Make sure the action record itself carries `authoringMode`.
     *
     * The list pages filter on that field, so a playbook whose record lacks it
     * would disappear from Playbooks and show up under Workflows. The create
     * dialog sets it, but a record reached another way (an older playbook, or a
     * pasted URL) may not have it - so the first save repairs it.
     */
    const ensureRecordMode = useCallback(async (appId, journeyId) => {
        try {
            // getAgentAction returns the record itself, not a {response} wrapper
            const action = await getAgentAction(appId, journeyId);
            if (!action || isPlaybookRecord(action)) return;
            await saveAgentActionSettings(appId, { ...action, authoringMode: AUTHORING_MODE_PLAYBOOK });
        } catch (e) {
            // Not fatal: the definition still saves, the record just keeps
            // whatever mode it had.
            console.warn('could not mark the record as a playbook', e);
        }
    }, []);

    /**
     * Make sure the journey record this playbook writes into actually exists.
     *
     * Creating a playbook writes an *action* record; the matching *journey*
     * record is created separately, and outside on-prem that happens
     * asynchronously (action/save publishes an EventBridge event). Everything
     * here - saving the definition, saving a variable - goes through
     * journey-service `saveConfig`, which throws `journey not found` when the
     * record is not there yet.
     *
     * So the record is ensured before the first write. This also repairs
     * playbooks created before the create payload carried `actionType`.
     */
    const ensureJourneyRecord = useCallback(async (appId, journeyId) => {
        try {
            const existing = await journeyGet(appId, journeyId);
            if (existing && existing.journeyId) return true;

            const { status } = await journeyCreate(appId, {
                journeyId,
                name: journeyId,
                description: 'Created for a playbook',
                // The create handler requires a trigger, and rejects an event
                // trigger with no events. `action` is what action/save uses for
                // the workflow it creates behind an agent action.
                trigger: { triggerType: 'event', events: ['action'] },
            });

            // 'journey already exists' means another write won the race, which
            // is exactly the state we wanted.
            return status === 200 || status === 400;
        } catch (e) {
            console.warn('could not ensure the journey record', e);
            return false;
        }
    }, []);

    const refreshHistory = useCallback(async (appId, journeyId) => {
        if (!appId || !journeyId) return;
        try {
            const status = await getWorkflowHistoryStatus(appId, journeyId);
            setHistory(prev => ({ ...prev, canUndo: !!status?.canUndo, canRedo: !!status?.canRedo }));
        } catch (e) {
            setHistory(prev => ({ ...prev, canUndo: false, canRedo: false }));
        }
    }, []);

    const save = useCallback(async (current, appId, journeyId, autoSave = true) => {
        if (!current || !appId || !journeyId || generationLock.current) return;

        const { stmDefinition, errors, warnings } = compilePlaybook(current);
        setCompileErrors(errors);
        setCompileWarnings(warnings || []);

        // An invalid playbook is never written. Saving a half-compiled
        // definition would leave the runtime with states pointing nowhere, and
        // the author would not find out until they tested the agent.
        if (errors.length > 0 || !stmDefinition) return;

        let finishPending;
        const pending = new Promise(resolve => { finishPending = resolve; });
        pendingSaves.current.add(pending);
        setSyncing(true);
        setSaveSuccess(false);
        setSaveError(null);
        try {
            await ensureJourneyRecord(appId, journeyId);
            await journeyDefinitionSave(
                appId,
                journeyId,
                stmDefinition,
                {
                    authoringMode: AUTHORING_MODE_PLAYBOOK,
                    playbook: current,
                    // The canvas keys off metadata.nodes; blanking them keeps it
                    // from rendering a stale graph for a playbook-owned journey.
                    nodes: [],
                    edges: [],
                },
                autoSave,
            );
            await ensureRecordMode(appId, journeyId);
            setSavedPlaybook(current);
            setSaveSuccess(true);
            // A manual save is a new history point. The server records it
            // asynchronously, so check again a little later as the canvas does.
            if (!autoSave) {
                refreshHistory(appId, journeyId);
                clearTimeout(historyTimer.current);
                historyTimer.current = setTimeout(() => refreshHistory(appId, journeyId), 5000);
            }
        } catch (e) {
            console.error('playbook save failed', e);
            setSaveError(e.message || 'Failed to save this playbook');
        } finally {
            pendingSaves.current.delete(pending);
            finishPending();
            setSyncing(false);
        }
    }, [ensureRecordMode, ensureJourneyRecord, refreshHistory]);

    // Give `load` (declared above) access to the real save function.
    useEffect(() => { saveRef.current = save; }, [save]);

    const hasUnsavedChanges = loadState === LOAD_STATE.READY && !!playbook && playbook !== savedPlaybook;

    // Check edits as they happen, so problems show before the author saves.
    useEffect(() => {
        if (!hasUnsavedChanges) return;
        const { errors, warnings } = compilePlaybook(playbook);
        setCompileErrors(errors);
        setCompileWarnings(warnings || []);
    }, [playbook, hasUnsavedChanges]);

    const saveNow = useCallback(
        () => save(playbook, metadata.appId, metadata.journeyId, false),
        [save, playbook, metadata.appId, metadata.journeyId],
    );

    // Auto save: write edits shortly after the author stops making them.
    const autoSave = useMemo(() => debounce(save, AUTO_SAVE_DELAY), [save]);
    useEffect(() => {
        autoSaveRef.current = autoSave;
        return () => autoSave.cancel();
    }, [autoSave]);
    useEffect(() => {
        if (!autoSaveEnabled || !hasUnsavedChanges) return;
        autoSave(playbook, metadata.appId, metadata.journeyId, true);
    }, [autoSaveEnabled, hasUnsavedChanges, playbook, metadata.appId, metadata.journeyId, autoSave]);

    const setAutoSave = useCallback(enabled => {
        setAutoSaveEnabled(enabled);
        try { localStorage.setItem(AUTO_SAVE_STORAGE_KEY, JSON.stringify(enabled)); }
        catch (e) { /* the choice just isn't remembered */ }
    }, []);

    useEffect(() => {
        if (loadState === LOAD_STATE.READY) refreshHistory(metadata.appId, metadata.journeyId);
    }, [loadState, metadata.appId, metadata.journeyId, refreshHistory]);
    useEffect(() => () => clearTimeout(historyTimer.current), []);

    /**
     * Step back or forward through saved versions. The server restores the
     * version; the editor then reloads it, replacing any unsaved edits.
     */
    const stepHistory = useCallback(async direction => {
        const { appId, journeyId } = metadata;
        if (!appId || !journeyId || generationLock.current) return;
        autoSave.cancel();
        setHistory(prev => ({ ...prev, busy: true }));
        setSaveError(null);
        try {
            const restore = direction === 'undo' ? undoWorkflowChange : redoWorkflowChange;
            if (!await restore(appId, journeyId)) throw new Error(`Could not ${direction} the last change. Please try again.`);
            const restored = (await journeyGet(appId, journeyId))?.stm?.metadata?.playbook;
            if (!restored) throw new Error('That version has no playbook to restore.');
            setPlaybook(restored);
            setSavedPlaybook(restored);
            setCompileErrors([]);
            setCompileWarnings([]);
        } catch (e) {
            setSaveError(e.message);
        } finally {
            await refreshHistory(appId, journeyId);
            setHistory(prev => ({ ...prev, busy: false }));
        }
    }, [metadata, autoSave, refreshHistory]);

    /** Every mutation goes through here so unsaved-change tracking is automatic. */
    const update = useCallback(mutator => {
        if (generationLock.current) return;
        setPlaybook(prev => {
            if (!prev) return prev;
            const draft = JSON.parse(JSON.stringify(prev));
            mutator(draft);
            return draft;
        });
    }, []);

    const findSteps = (draft, sectionId, branchOf) => {
        const section = draft.sections.find(s => s.id === sectionId);
        if (!section) return null;
        if (!branchOf) return section.steps;

        // A branch list is addressed by the IF step that owns it.
        let found = null;
        const walk = steps => {
            steps.forEach(step => {
                (step.branches || []).forEach(branch => {
                    if (branch.id === branchOf) found = branch.steps;
                    else walk(branch.steps);
                });
            });
        };
        walk(section.steps);
        return found;
    };

    const saveSettings = async patch => {
        if (generationLock.current) throw new Error('Wait for AI generation to finish before editing settings.');
        if (settingsBusy.current) throw new Error('Another setting is being saved. Please try again.');
        settingsBusy.current = true;
        setSettingsSaving(true);
        try {
            const latest = await getAgentAction(metadata.appId, metadata.journeyId);
            if (!latest?.id) throw new Error('Could not load playbook settings. Please try again.');
            const updated = { ...latest, ...patch, id: latest.id, authoringMode: AUTHORING_MODE_PLAYBOOK };
            updated.name = updated.name?.trim();
            if (!updated.name) throw new Error('Give the playbook a name.');
            const { status } = await saveAgentActionSettings(metadata.appId, updated);
            if (status !== 200) throw new Error('Could not save playbook settings. Please try again.');
            await syncPlaybookTool(metadata.appId, updated);
            setAction(updated);
            setSaveError(null);
        } finally {
            settingsBusy.current = false;
            setSettingsSaving(false);
        }
    };

    const beginGeneration = async request => {
        if (generationLock.current || settingsBusy.current) throw new Error('Wait for the current operation to finish.');
        generationLock.current = true;
        setAiStarting(true);
        autoSave.cancel();
        try {
            await Promise.all([...pendingSaves.current]);
            const result = await startPlaybookGeneration(metadata.appId, {
                ...request, journeyId: metadata.journeyId,
                ...(!request.startFromScratch ? { playbook } : {}),
            });
            setAiGenStatus(result.aiGenStatus);
            generationLock.current = isGenerationActive(result.aiGenStatus);
            setAiPollError('');
        } catch (error) {
            generationLock.current = false;
            throw error;
        }
        finally { setAiStarting(false); }
    };

    const aiActive = isGenerationActive(aiGenStatus);
    useEffect(() => {
        if (!aiActive || !metadata.appId || !metadata.journeyId) return undefined;
        let stopped = false;
        let timer;
        const poll = async () => {
            try {
                const next = await getPlaybookGenerationStatus(metadata.appId, metadata.journeyId);
                if (stopped) return;
                setAiGenStatus(next);
                setAiPollError('');
                if (!isGenerationActive(next)) {
                    generationLock.current = false;
                    if (next?.state === 'complete') await load(metadata.appId, metadata.journeyId);
                    return;
                }
            } catch (error) { if (!stopped) setAiPollError(error.message); }
            if (!stopped) timer = setTimeout(poll, 2500);
        };
        poll();
        return () => { stopped = true; clearTimeout(timer); };
    }, [aiActive, metadata.appId, metadata.journeyId, load]);

    const api = {
        action, saveSettings, settingsSaving: settingsSaving || aiStarting || aiActive,
        aiGenStatus, aiBusy: aiStarting || aiActive, aiPollError, beginGeneration,
        playbook,
        metadata,
        setMetadata,
        loadState,
        load,
        saveNow,
        hasUnsavedChanges,
        autoSaveEnabled,
        setAutoSave,
        canUndo: history.canUndo,
        canRedo: history.canRedo,
        historyBusy: history.busy,
        undo: () => stepHistory('undo'),
        redo: () => stepHistory('redo'),
        syncing,
        saveError,
        saveSuccess,
        compileErrors,
        compileWarnings,
        errorsByStep,
        selectedStepId,
        setSelectedStepId,

        addSection: () => update(d => {
            d.sections.push(createSection(`Section ${d.sections.length + 1}`));
        }),

        renameSection: (sectionId, title) => update(d => {
            const section = d.sections.find(s => s.id === sectionId);
            if (section) section.title = title;
        }),

        removeSection: sectionId => update(d => {
            d.sections = d.sections.filter(s => s.id !== sectionId);
            if (d.sections.length === 0) d.sections = [createSection('Section 1')];
        }),

        addStep: (sectionId, kind, branchOf = null, atIndex = null) => {
            const step = createStep(kind);
            update(d => {
                const steps = findSteps(d, sectionId, branchOf);
                if (!steps) return;
                if (atIndex === null || atIndex > steps.length) steps.push(step);
                else steps.splice(atIndex, 0, step);
            });
            setSelectedStepId(step.id);
            return step;
        },

        updateStepKind: (stepId, kind) => update(d => {
            walk(d, step => {
                if (step.id === stepId) Object.assign(step, changeStepKind(step, kind));
            });
        }),

        updateStepConfig: (stepId, config) => update(d => {
            walk(d, step => {
                if (step.id === stepId) step.config = { ...step.config, ...config };
            });
        }),

        removeStep: stepId => {
            update(d => {
                const prune = steps => {
                    const index = steps.findIndex(s => s.id === stepId);
                    if (index >= 0) { steps.splice(index, 1); return true; }
                    return steps.some(s => (s.branches || []).some(b => prune(b.steps)));
                };
                d.sections.forEach(section => prune(section.steps));
            });
            setSelectedStepId(prev => (prev === stepId ? null : prev));
        },

        /** Reorder within one list (section body or branch body). */
        moveStep: (sectionId, branchOf, fromId, toId) => update(d => {
            const steps = findSteps(d, sectionId, branchOf);
            if (!steps) return;
            const from = steps.findIndex(s => s.id === fromId);
            const to = steps.findIndex(s => s.id === toId);
            if (from < 0 || to < 0 || from === to) return;
            const [moved] = steps.splice(from, 1);
            steps.splice(to, 0, moved);
        }),

        addBranch: (stepId, conditionType = 'natural') => update(d => {
            walk(d, step => {
                if (step.id !== stepId) return;
                step.branches = appendBranch(step.branches || [], conditionType);
            });
        }),

        updateBranch: (branchId, patch) => update(d => {
            walk(d, step => {
                (step.branches || []).forEach(branch => {
                    if (branch.id === branchId) Object.assign(branch, typeof patch === 'string' ? { condition: patch } : patch);
                });
            });
        }),

        removeBranch: branchId => update(d => {
            walk(d, step => {
                if (step.branches) step.branches = step.branches.filter(b => b.id !== branchId);
            });
        }),
    };

    return <PlaybookContext.Provider value={api}>{children}</PlaybookContext.Provider>;
}

/** Visit every step in the document, including those nested in IF branches. */
function walk(draft, visit) {
    const visitAll = steps => {
        (steps || []).forEach(step => {
            visit(step);
            (step.branches || []).forEach(branch => visitAll(branch.steps));
        });
    };
    (draft.sections || []).forEach(section => visitAll(section.steps));
}

export default PlaybookContextProvider;
